import React, {useMemo} from "react";
import ReactEcharts from "echarts-for-react";
import {IDailyData} from "../models/roi-widget.state";

interface IROILineChartProps {
    currentData: IDailyData[];
    previousData?: IDailyData[];
    metric: "count" | "timeSaved" | "costSaved";
    currency?: string;
    showComparison?: boolean;
    height?: number;
}

const ROILineChart: React.FC<IROILineChartProps> = ({
    currentData,
    previousData,
    metric,
    currency = "USD",
    showComparison = false,
    height = 200,
}) => {
    const chartOptions = useMemo(() => {
        // Create a unified timeline based on current period dates
        const categories = currentData.map((item) => {
            const date = new Date(item.day);
            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
        });

        // Prepare current period values
        const currentValues = currentData.map((item) => {
            switch (metric) {
                case "timeSaved":
                    return item.timeSaved;
                case "costSaved":
                    return item.costSaved;
                default:
                    return item.count;
            }
        });

        // Prepare previous period values aligned to the same timeline
        const previousValues =
            showComparison && previousData
                ? currentData.map((currentItem, index) => {
                      // Try to find corresponding data point in previous period
                      // For now, use index-based mapping, but this could be enhanced
                      // to do date-based alignment if needed
                      if (previousData[index]) {
                          switch (metric) {
                              case "timeSaved":
                                  return previousData[index].timeSaved;
                              case "costSaved":
                                  return previousData[index].costSaved;
                              default:
                                  return previousData[index].count;
                          }
                      }
                      return 0; // Default value if no corresponding data
                  })
                : [];

        // Get metric label and unit
        const getMetricInfo = () => {
            switch (metric) {
                case "timeSaved":
                    return {label: "Time Saved", unit: " hrs"};
                case "costSaved":
                    return {label: "Cost Saved", unit: currency === "USD" ? " $" : ` ${currency}`};
                default:
                    return {label: "Activities", unit: ""};
            }
        };

        const metricInfo = getMetricInfo();

        const series = [
            {
                name: "Current Period",
                type: "line",
                data: currentValues,
                smooth: true,
                symbol: "circle",
                symbolSize: 8,
                lineStyle: {
                    color: "#29ABE2",
                    width: 3,
                },
                itemStyle: {
                    color: "#29ABE2",
                    borderColor: "#29ABE2",
                    borderWidth: 2,
                },
                // No areaStyle for clean lines like Figma
            },
        ];

        // Add previous period series if comparison is enabled
        if (showComparison && previousValues.length > 0) {
            series.push({
                name: "Previous Period",
                type: "line",
                data: previousValues,
                smooth: true,
                symbol: "diamond",
                symbolSize: 8,
                lineStyle: {
                    color: "#E64E7C",
                    width: 1,
                } as any,
                itemStyle: {
                    color: "#E64E7C",
                    borderColor: "#E64E7C",
                    borderWidth: 2,
                },
                // No areaStyle for clean dashed lines like Figma
            });
        }

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "cross",
                    label: {
                        backgroundColor: "#6a7985",
                    },
                },
                formatter: (params: any) => {
                    let tooltip = `<div style="margin-bottom: 5px;">${params[0].axisValue}</div>`;
                    params.forEach((param: any) => {
                        const value =
                            metric === "costSaved"
                                ? `${metricInfo.unit}${param.value.toLocaleString()}`
                                : `${param.value.toLocaleString()}${metricInfo.unit}`;
                        tooltip += `
                            <div style="display: flex; align-items: center; margin-bottom: 2px;">
                                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                                <span style="margin-right: 8px;">${param.seriesName}:</span>
                                <span style="font-weight: bold;">${value}</span>
                            </div>
                        `;
                    });
                    return tooltip;
                },
            },
            legend: {
                data: showComparison ? ["Current Period", "Previous Period"] : ["Current Period"],
                top: 10,
                textStyle: {
                    color: "#666",
                },
            },
            grid: {
                left: "10%",
                right: "10%",
                bottom: "15%",
                top: showComparison ? "20%" : "15%",
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: categories,
                boundaryGap: false,
                axisLine: {
                    lineStyle: {
                        color: "#e0e0e0",
                    },
                },
                axisLabel: {
                    color: "#666",
                    fontSize: 12,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "#f5f5f5",
                        type: "dashed" as any,
                    },
                },
            },
            yAxis: {
                type: "value",
                axisLine: {
                    lineStyle: {
                        color: "#e0e0e0",
                    },
                },
                axisLabel: {
                    color: "#666",
                    fontSize: 12,
                    formatter: (value: number) => {
                        if (metric === "costSaved") {
                            return `${metricInfo.unit}${value.toLocaleString()}`;
                        }
                        return `${value.toLocaleString()}${metricInfo.unit}`;
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: "#f5f5f5",
                        type: "dashed",
                    },
                },
            },
            series,
        };
    }, [currentData, previousData, metric, currency, showComparison]);

    return (
        <div className="roi-widget__chart">
            <ReactEcharts
                option={chartOptions}
                style={{height: `${height}px`, width: "100%"}}
                opts={{renderer: "canvas"}}
            />
        </div>
    );
};

export default ROILineChart;
